import Loading from "components/Loading";

import localLawService from "api/localLawService";
import { ELocalLawAccess } from "dto/LocalLaw/ILocalLaw";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

// Prawo miejscowe → „Uprawnienia". Odwzorowanie flag User.LocalLawPermissions
// (Kwantum.Enums.LocalLawAccess) z Magistratu, które decydowały, kto prowadzi akt
// po akceptacji: uchwalenie, przekazanie do publikacji, ZIPX, akty obowiązujące.
//
// Właściciel profilu ma pełen dostęp z definicji i nie da się mu go odebrać —
// inaczej moduł dałoby się zablokować w całości. Tak samo administrator z metryki
// pracownika (Magistrat: IsAdmin). Te same flagi ustawia się też w metryce pracownika
// (Ustawienia → Zespół → Lista osób → „Edytuj" w menu członka).

const errorText = (e: unknown, fallback: string) => {
  const data = (
    e as {
      response?: {
        data?: { errorCodes?: { message?: string }[]; Message?: string };
      };
    }
  )?.response?.data;
  return data?.errorCodes?.[0]?.message ?? data?.Message ?? fallback;
};

const FLAGS: { flag: ELocalLawAccess; label: string; hint: string }[] = [
  {
    flag: ELocalLawAccess.ManageResolutions,
    label: "Uchwały",
    hint: "Prowadzi obieg uchwał rady",
  },
  {
    flag: ELocalLawAccess.ManageOrdinances,
    label: "Zarządzenia",
    hint: "Prowadzi obieg zarządzeń",
  },
  {
    flag: ELocalLawAccess.BasicManagement,
    label: "Nadzór ogólny",
    hint: "Pełen dostęp do wszystkich rodzajów aktów",
  },
];

const PermissionsView = () => {
  const coworkersQuery = localLawService.useGetCoworkers();
  const { mutate: savePermission } = localLawService.useSavePermission();

  const coworkers = coworkersQuery.data?.data ?? [];

  const toggle = (userId: string, current: number, flag: ELocalLawAccess) => {
    // Jak w metryce pracownika (Magistrat, EditUserViewModel): „Nadzór ogólny" obejmuje
    // uchwały i zarządzenia, więc wyklucza się z pozostałymi flagami.
    const next =
      current & flag
        ? current & ~flag
        : flag === ELocalLawAccess.BasicManagement
          ? ELocalLawAccess.BasicManagement
          : (current & ~ELocalLawAccess.BasicManagement) | flag;

    savePermission(
      { UserId: userId, Access: next },
      {
        onSuccess: () => coworkersQuery.refetch(),
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się zapisać uprawnień."),
          }),
      },
    );
  };

  if (coworkersQuery.isLoading) return <Loading />;

  return (
    <div className={styles.panel}>
      <div className={styles.topBar}>
        <span className={styles.listHeading}>Uprawnienia biura rady</span>
      </div>

      <div className={styles.listWrap}>
        {coworkers.map((c) => (
          <div key={c.UserId} className={styles.permissionRow}>
            <span className={styles.permissionName}>
              {c.Name}
              {c.Email ? ` · ${c.Email}` : ""}
              {c.IsProfileOwner && (
                <span className={`${styles.badge} ${styles.badgeStatus}`}>
                  właściciel profilu
                </span>
              )}
              {!c.IsProfileOwner && c.HasFullAccess && (
                <span className={`${styles.badge} ${styles.badgeStatus}`}>
                  administrator
                </span>
              )}
            </span>

            <div className={styles.permissionFlags}>
              {FLAGS.map((f) => (
                <label key={f.flag} className={styles.checkboxRow} title={f.hint}>
                  <input
                    type="checkbox"
                    checked={!!(c.Access & f.flag)}
                    disabled={c.IsProfileOwner || c.HasFullAccess}
                    onChange={() => toggle(c.UserId, c.Access, f.flag)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
        ))}

        {!coworkers.length && (
          <div className={styles.emptyList}>
            W Twojej przestrzeni roboczej nie ma innych osób.
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionsView;
